import { Authenticated, Public } from 'modules/router/types';

// LOGIN
import AuthLogin from 'modules/v2/login/pages/Login';
import AuthReset from 'modules/v2/login/containers/ForgotPassword';
import AuthToken from 'modules/v2/login/pages/AutoLoginCheck';
import AuthSetPassword from 'modules/v2/login/pages/AuthSetPassword';
import AuthNewPassword from 'modules/v2/login/containers/NewPassword.jsx';

import {
  BookCatalog,
  BookPerformanceAnalytics,
  CampaignHistory,
  CampaignMetrics,
  CreateCustomerTemplate,
  CreateEmailCampaign,
  CreateOnDemandEmail,
  EditCustomerTemplate,
  EmailCampaign,
  EmailCampaignAnalytics,
  EmailHistory,
  EmailTemplates,
  HelpCenter,
  HomePage,
  ImportLeads,
  LaunchEmailCampaign,
  Leads,
  MarketingHub,
  OnDemandEmail,
  PageProfile,
  Password,
  PlansAndBilling,
  Profile,
  RedirectReferralMarketing,
  ReferralMarketingMagazinesPage,
  ReleaseNotes,
  SenderEmailConfiguration,
  ShippingAddress,
  SocialMediaTrainingSite,
  TrainingSite,
  Unsubscribe,
  ProjectEditor,
  MyProjects,
  LegacyMyProjects,
  MySetup,
  TemplatesPage,
} from 'modules/v2/pages/';

import {
  GeneratedMagazinePage,
  MagazineRecipientsPage,
  PreviousMagazinesPage,
} from 'modules/v2/pages/ReferralMarketingMagazines/components';
import MagazineEditorPage from 'modules/v2/pages/ReferralMarketingMagazines/components/MagazineEditor';

import dentistRoutes from 'modules/v2/routes/dentist';
import GhlCallback from 'modules/v2/pages/MarketingHub/GhlCallback';
import FusionAuthSilentCallback from 'modules/v2/pages/MarketingHub/FusionAuthSilentCallback';

import OrderedBooksListingPage from 'v2/pages/OrderedBooksListingPage';
import BuyCreditsJourney from 'v2/pages/BuyCredits';
import {
  MailOnDemandWizardPage,
  MailOnDemandOrdersPage,
  MailOnDemandOrderDetailPage,
  MailOnDemandCreditsPage,
} from 'v2/pages/MailOnDemand';

import env from 'environments';

import {
  REFERRAL_MARKETING,
  ROUTE_ALL_CAMPAIGN_METRICS,
  ROUTE_BOOK_CATALOG,
  ROUTE_BOOK_PERFORMANCE,
  ROUTE_BUY_CREDITS,
  ROUTE_CAMPAIGNS_HISTORY,
  ROUTE_CHANGE_PASSWORD,
  ROUTE_CREATE_CUSTOMER_TEMPLATE,
  ROUTE_CREATE_EMAIL_CAMPAIGN,
  ROUTE_CREATE_ON_DEMAND_EMAIL,
  ROUTE_DIGITAL_BOOKS,
  ROUTE_EDIT_CUSTOMER_TEMPLATE,
  ROUTE_EDIT_SHIPPING_ADDRESS,
  ROUTE_EMAIL_CAMPAIGN,
  ROUTE_EMAIL_CAMPAIGNS_ANALYTICS,
  ROUTE_EMAIL_HISTORY,
  ROUTE_EMAIL_LAUNCH_CAMPAIGN,
  ROUTE_EMAIL_SENDER_EMAIL_CONFIGURATION,
  ROUTE_EMAIL_TEMPLATES,
  ROUTE_FUSIONAUTH_SILENT_CALLBACK,
  ROUTE_GHL_CALLBACK,
  ROUTE_HELP_CENTER,
  ROUTE_HOMEPAGE,
  ROUTE_IMPORT_LEADS,
  ROUTE_LEADS,
  ROUTE_MANAGE_BOOK_VERSION,
  ROUTE_MARKETING_HUB,
  ROUTE_MY_ORDERS,
  ROUTE_ON_DEMAND_EMAIL,
  ROUTE_PAGE_PROFILE,
  ROUTE_PLANS_AND_BILLING,
  ROUTE_PROFILE,
  ROUTE_REFERRAL_MARKETING_GENERATED_MAGAZINE,
  ROUTE_REFERRAL_MARKETING_MAGAZINE_EDITOR,
  ROUTE_REFERRAL_MARKETING_MAGAZINE_RECIPIENTS,
  ROUTE_REFERRAL_MARKETING_PREVIOUS_MAGAZINES,
  ROUTE_REFERRAL_MARKETING_REDIRECT_ROUTE,
  ROUTE_RELEASE_NOTES,
  ROUTE_SOCIAL_MEDIA_TRAINING_SITE,
  ROUTE_TRAINING_SITE,
  ROUTE_UNSUBSCRIBE,
  ROUTE_DASHBOARD_TEMPLATES,
  ROUTE_DASHBOARD_MY_PROJECTS,
  ROUTE_DASHBOARD_SETUP_DEFAULTS,
} from './constants';

import ManageBookVersion from '../pages/ManageBookVersion';
import { welcomeJourneyRedirectRoute } from './welcomeJourneyRedirect';
import { legacyBillingRedirectRoutes } from './legacyBillingRedirects';
import { mailOnDemandRoutes } from './mailOnDemandRoutes';

const templateBuilderRoutes = () => {
  if (!env.TEMPLATE_BUILDER_ENABLED) return [];

  return [
    {
      type: Authenticated,
      exact: true,
      path: '/templates/create/:id',
      component: ProjectEditor,
    },

    {
      type: Authenticated,
      exact: true,
      path: '/templates/project/:id',
      component: ProjectEditor,
    },
  ];
};

const regularRoutes = [
  welcomeJourneyRedirectRoute,
  ...legacyBillingRedirectRoutes,
  ...dentistRoutes,
  ...templateBuilderRoutes(),

  {
    type: Authenticated,
    exact: true,
    path: ROUTE_DASHBOARD_MY_PROJECTS,
    component: MyProjects,
  },

  {
    type: Authenticated,
    exact: true,
    path: ROUTE_DASHBOARD_SETUP_DEFAULTS,
    component: MySetup,
  },

  {
    type: Authenticated,
    exact: true,
    path: ROUTE_DASHBOARD_TEMPLATES,
    component: TemplatesPage,
  },

  {
    type: Public,
    exact: true,
    path: '/auth/login',
    component: AuthLogin,
  },
  {
    type: Public,
    exact: true,
    path: '/auth/reset',
    component: AuthReset,
  },
  {
    type: Public,
    exact: true,
    path: '/auth/token',
    component: AuthToken,
  },
  {
    type: Public,
    exact: true,
    path: '/auth/setpassword',
    component: AuthSetPassword,
  },
  {
    type: Public,
    exact: true,
    path: '/auth/newpassword',
    component: AuthNewPassword,
  },

  // UNSUBSCRIBE
  {
    type: Public,
    exact: true,
    path: ROUTE_UNSUBSCRIBE,
    component: Unsubscribe,
  },

  // MY BOOKS
  {
    type: Authenticated,
    exact: true,
    path: ROUTE_BOOK_CATALOG,
    component: BookCatalog,
  },
  {
    type: Authenticated,
    exact: true,
    path: ROUTE_MY_ORDERS,
    component: OrderedBooksListingPage,
  },
  {
    type: Authenticated,
    exact: true,
    path: ROUTE_DIGITAL_BOOKS,
    component: OrderedBooksListingPage,
  },
  {
    type: Authenticated,
    exact: true,
    path: ROUTE_BOOK_PERFORMANCE,
    component: BookPerformanceAnalytics,
  },
  {
    type: Authenticated,
    exact: true,
    path: ROUTE_MANAGE_BOOK_VERSION,
    component: ManageBookVersion,
  },

  // BUY CREDITS
  {
    type: Authenticated,
    exact: true,
    path: ROUTE_BUY_CREDITS,
    component: BuyCreditsJourney,
  },

  // MAIL ON DEMAND (MOD): hidden unless VITE_ENABLE_MOD is 'true'
  ...mailOnDemandRoutes({
    OrdersPage: MailOnDemandOrdersPage,
    WizardPage: MailOnDemandWizardPage,
    CreditsPage: MailOnDemandCreditsPage,
    OrderDetailPage: MailOnDemandOrderDetailPage,
  }),

  // Marketing Hub (GoHighLevel)
  {
    path: ROUTE_MARKETING_HUB,
    component: MarketingHub,
    exact: true,
    type: Authenticated,
  },
  // GHL Session Bridge Callback (handles FusionAuth redirect)
  {
    path: ROUTE_GHL_CALLBACK,
    component: GhlCallback,
    exact: true,
    type: Public,
  },
  // FusionAuth Silent Callback (for background session establishment)
  {
    path: ROUTE_FUSIONAUTH_SILENT_CALLBACK,
    component: FusionAuthSilentCallback,
    exact: true,
    type: Public,
  },

  // Campaigns
  {
    path: ROUTE_LEADS,
    component: Leads,
    exact: true,
    type: Authenticated,
  },
  {
    path: ROUTE_IMPORT_LEADS,
    component: ImportLeads,
    exact: true,
    type: Authenticated,
  },
  {
    path: ROUTE_EMAIL_TEMPLATES,
    component: EmailTemplates,
    exact: true,
    type: Authenticated,
  },
  {
    path: ROUTE_EDIT_CUSTOMER_TEMPLATE,
    component: EditCustomerTemplate,
    exact: true,
    type: Authenticated,
  },
  {
    path: ROUTE_CREATE_CUSTOMER_TEMPLATE,
    component: CreateCustomerTemplate,
    exact: true,
    type: Authenticated,
  },
  {
    path: ROUTE_EMAIL_SENDER_EMAIL_CONFIGURATION,
    component: SenderEmailConfiguration,
    exact: true,
    type: Authenticated,
  },
  {
    path: ROUTE_EMAIL_HISTORY,
    component: EmailHistory,
    exact: true,
    type: Authenticated,
  },
  {
    path: ROUTE_EMAIL_CAMPAIGNS_ANALYTICS,
    component: EmailCampaignAnalytics,
    exact: true,
    type: Authenticated,
  },
  {
    path: ROUTE_EMAIL_LAUNCH_CAMPAIGN,
    component: LaunchEmailCampaign,
    exact: true,
    type: Authenticated,
  },
  {
    path: ROUTE_CREATE_EMAIL_CAMPAIGN,
    component: CreateEmailCampaign,
    exact: true,
    type: Authenticated,
  },
  {
    path: ROUTE_CAMPAIGNS_HISTORY,
    component: CampaignHistory,
    exact: true,
    type: Authenticated,
  },
  {
    path: ROUTE_ALL_CAMPAIGN_METRICS,
    component: CampaignMetrics,
    exact: true,
    type: Authenticated,
  },
  {
    path: ROUTE_CREATE_ON_DEMAND_EMAIL,
    component: CreateOnDemandEmail,
    exact: true,
    type: Authenticated,
  },
  {
    path: ROUTE_ON_DEMAND_EMAIL,
    component: OnDemandEmail,
    exact: true,
    type: Authenticated,
  },
  {
    path: ROUTE_EMAIL_CAMPAIGN,
    component: EmailCampaign,
    exact: true,
    type: Authenticated,
  },

  // CUSTOMER / PLAN PAGES
  {
    path: ROUTE_PLANS_AND_BILLING,
    component: PlansAndBilling,
    exact: true,
    type: Authenticated,
  },
  {
    type: Authenticated,
    exact: true,
    path: ROUTE_HELP_CENTER,
    component: HelpCenter,
  },
  {
    type: Authenticated,
    exact: true,
    path: ROUTE_PROFILE,
    component: Profile,
  },
  {
    type: Authenticated,
    exact: true,
    path: ROUTE_RELEASE_NOTES,
    component: ReleaseNotes,
  },
  {
    type: Authenticated,
    path: ROUTE_EDIT_SHIPPING_ADDRESS,
    component: ShippingAddress,
    exact: true,
  },
  {
    type: Authenticated,
    path: ROUTE_PAGE_PROFILE,
    component: PageProfile,
    exact: true,
  },
  {
    type: Authenticated,
    path: ROUTE_CHANGE_PASSWORD,
    component: Password,
    exact: true,
  },

  // HOME PAGE
  {
    type: Authenticated,
    exact: true,
    path: ROUTE_HOMEPAGE,
    component: HomePage,
  },

  //  SOCIAL MEDIA TRAINING
  {
    type: Authenticated,
    exact: true,
    path: ROUTE_SOCIAL_MEDIA_TRAINING_SITE,
    component: SocialMediaTrainingSite,
  },

  // TRAINING SITE
  {
    type: Authenticated,
    exact: true,
    path: ROUTE_TRAINING_SITE,
    component: TrainingSite,
  },

  // REFERRAL MARKETING
  {
    type: Authenticated,
    path: REFERRAL_MARKETING,
    component: ReferralMarketingMagazinesPage,
    children: [
      {
        exact: true,
        path: ROUTE_REFERRAL_MARKETING_MAGAZINE_EDITOR,
        component: MagazineEditorPage,
      },
      {
        exact: true,
        path: ROUTE_REFERRAL_MARKETING_GENERATED_MAGAZINE,
        component: GeneratedMagazinePage,
      },
      {
        exact: true,
        path: ROUTE_REFERRAL_MARKETING_PREVIOUS_MAGAZINES,
        component: PreviousMagazinesPage,
      },
      {
        exact: true,
        path: ROUTE_REFERRAL_MARKETING_MAGAZINE_RECIPIENTS,
        component: MagazineRecipientsPage,
      },
      {
        exact: true,
        path: ROUTE_REFERRAL_MARKETING_REDIRECT_ROUTE,
        component: RedirectReferralMarketing,
      },
    ],
  },
];

export const routes = [].concat(regularRoutes);
